import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { ICellRendererParams, RowClickedEvent } from 'ag-grid-community'
import { Copy, Image as ImageIcon, LayoutGrid, List, Pencil, Tags, Trash2, UploadCloud, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import {
	useCreateAssetTag,
	useDeleteAsset,
	useRemoveTagFromAssets,
	useRenameAssetTag,
	useUpdateAsset
} from '@/api/api-mutations'
import type { Asset, AssetCategoryType, AssetStatusType, AssetUsage, AssetUsageFilterType } from '@/api/common-types'
import { useListAssets, useListAssetTags, useListMarketingChannels, useListMarketingContent } from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/base/dialog'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { SearchField } from '@/components/base/search-field'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { Thumbnail } from '@/components/base/thumbnail'
import { toast } from '@/components/base/toast'
import { useAppForm } from '@/components/form/form'
import { SingleColumnPage } from '@/components/page-layout'
import { AdvancedFilterDrawer } from '@/components/table/advanced-filter-drawer'
import { FilterButton } from '@/components/table/filter-button'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { createActionsColumn, Table, type TableColDef } from '@/components/table/table'
import { useAssetUpload } from '@/hooks/use-asset-upload'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'
import { clx } from '@/utils/clx'

// The Products and Marketing `LibraryPage` translation objects are structurally identical, so either
// can type the shared `copy` prop.
type LibraryCopy = ReturnType<typeof useI18n>['L']['Products']['LibraryPage']

type AssetLibraryView = 'table' | 'gallery'

// Reserved sentinel shared with the API's marketing filters: "No channel" / "No content".
// Mutually exclusive with concrete uuids.
const FILTER_NONE = 'NONE'

// Enforce sentinel exclusivity on a multi-select value: the newest selection is last, so if the
// sentinel was just picked keep only it, otherwise drop the sentinel in favour of the concrete uuids.
const normalizeSentinel = (values: string[]): string[] => {
	if (values.length === 0) return values
	if (values[values.length - 1] === FILTER_NONE) return [FILTER_NONE]
	return values.filter(v => v !== FILTER_NONE)
}

type AssetLibrarySearch = {
	limit?: number
	offset?: number
	q?: string
	tags?: string[]
	usage?: AssetUsageFilterType
	channels?: string[]
	content?: string[]
	view?: AssetLibraryView
}

export function AssetLibraryPage({
	category,
	heading,
	copy
}: {
	category: AssetCategoryType
	heading: string
	copy: LibraryCopy
}) {
	const { L } = useI18n()
	const navigate = useNavigate()
	const search = useSearch({ strict: false }) as AssetLibrarySearch

	const isMarketing = category === 'MARKETING'
	const limit = search.limit ?? DEFAULT_PAGE_SIZE
	const offset = search.offset ?? 0
	const q = search.q?.trim() || undefined
	const selectedTags = useMemo(() => search.tags ?? [], [search.tags])
	const usageFilter = search.usage
	const selectedChannels = useMemo(() => search.channels ?? [], [search.channels])
	const selectedContent = useMemo(() => search.content ?? [], [search.content])
	const view: AssetLibraryView = search.view ?? 'table'

	const [uploadOpen, setUploadOpen] = useState(false)
	const [manageTagsOpen, setManageTagsOpen] = useState(false)
	const [filtersOpen, setFiltersOpen] = useState(false)
	// Server-side search term for the marketing content combobox (reset when the drawer closes so a
	// reopened drawer starts fresh, matching the combobox's own cleared input).
	const [contentSearch, setContentSearch] = useState('')
	useEffect(() => {
		if (!filtersOpen) setContentSearch('')
	}, [filtersOpen])
	const [editingAsset, setEditingAsset] = useState<Asset | null>(null)
	const [previewAsset, setPreviewAsset] = useState<Asset | null>(null)

	const deleteAsset = useDeleteAsset()

	const assetsQuery = useListAssets({
		category: [category],
		tags: selectedTags.length > 0 ? selectedTags : undefined,
		usage: isMarketing ? undefined : usageFilter,
		channelUuids: selectedChannels.length > 0 ? selectedChannels : undefined,
		contentUuids: selectedContent.length > 0 ? selectedContent : undefined,
		q,
		limit,
		offset
	})
	const tagsQuery = useListAssetTags({ category })
	// Marketing-only: populate the channel/content filter comboboxes. Skipped for the product library.
	// Channels are few (no pagination); content can exceed a page, so it's searched server-side by name.
	const channelsQuery = useListMarketingChannels({ enabled: isMarketing })
	const contentQuery = useListMarketingContent(
		{ limit: 50, offset: 0, q: contentSearch || undefined },
		{ enabled: isMarketing }
	)

	const assets = useMemo(() => assetsQuery.data?.assets ?? [], [assetsQuery.data])
	const allTags = useMemo(() => (tagsQuery.data?.tags ?? []).map(t => t.name), [tagsQuery.data])
	const tagOptions = useMemo(() => allTags.map(tag => ({ value: tag, label: tag })), [allTags])

	// Product library only: assets are consumed by products/groups/categories. Marketing uses the
	// channel/content filters below instead.
	const usageOptions = useMemo(
		() => [
			{ value: 'UNUSED', label: copy.UsageUnused() },
			{ value: 'PRODUCT', label: copy.UsageProduct() },
			{ value: 'PRODUCT_GROUP', label: copy.UsageProductGroup() },
			{ value: 'PRODUCT_CATEGORY', label: copy.UsageProductCategory() }
		],
		[copy]
	)

	// Marketing filter options, each led by the "No channel" / "No content" sentinel.
	const channelOptions = useMemo(
		() => [
			{ value: FILTER_NONE, label: copy.ChannelNone() },
			...(channelsQuery.data ?? []).map(c => ({ value: c.uuid, label: c.name }))
		],
		[channelsQuery.data, copy]
	)
	const contentOptions = useMemo(
		() => [
			{ value: FILTER_NONE, label: copy.ContentNone() },
			...(contentQuery.data?.content ?? []).map(c => ({ value: c.uuid, label: c.name }))
		],
		[contentQuery.data, copy]
	)

	const hasActiveFilters =
		Boolean(q) ||
		selectedTags.length > 0 ||
		(isMarketing ? selectedChannels.length > 0 || selectedContent.length > 0 : Boolean(usageFilter))
	// Count only the drawer-controlled filters (search `q` is a separate field).
	const activeFilterCount =
		selectedTags.length + (isMarketing ? selectedChannels.length + selectedContent.length : usageFilter ? 1 : 0)
	const showingEmptySuccess = assetsQuery.isSuccess && assets.length === 0 && !hasActiveFilters

	const setView = (next: AssetLibraryView) => {
		navigate({ to: '.', search: (prev: any) => ({ ...prev, view: next }) })
	}

	const applyFilters = (next: { tags: string[]; usage?: string; channels?: string[]; content?: string[] }) => {
		navigate({
			to: '.',
			search: (prev: any) => ({
				...prev,
				tags: next.tags.length > 0 ? next.tags : undefined,
				usage: next.usage || undefined,
				channels: next.channels && next.channels.length > 0 ? next.channels : undefined,
				content: next.content && next.content.length > 0 ? next.content : undefined,
				offset: 0
			})
		})
	}

	const handleDelete = async (asset: Asset) => {
		if (!window.confirm(copy.DeleteAssetConfirm())) return
		try {
			await deleteAsset({ path: { assetUuid: asset.uuid } })
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleCopyUrl = async (asset: Asset) => {
		const url = pickCopyUrl(asset)
		if (!url) {
			toast.error(L.General.Unknown())
			return
		}
		try {
			await navigator.clipboard.writeText(url)
			toast.success(copy.CopiedUrl())
		} catch {
			toast.error(L.General.Unknown())
		}
	}

	const columnDefs = useMemo<TableColDef<Asset>[]>(
		() => [
			{
				headerName: copy.Filename(),
				field: 'filename',
				flex: 2,
				cellRenderer: ({ data }: ICellRendererParams<Asset>) =>
					data ? (
						<div className='flex h-full w-full items-center gap-x-3 overflow-hidden'>
							<div className='w-fit flex-shrink-0'>
								<Thumbnail src={pickThumbUrl(data)} />
							</div>
							<span title={data.filename} className='truncate'>
								{data.filename}
							</span>
						</div>
					) : null
			},
			{
				headerName: L.Fields.Status(),
				field: 'status',
				flex: 1,
				cellRenderer: ({ value }: { value: AssetStatusType }) => (
					<StatusCell color={statusColor(value)}>{value}</StatusCell>
				)
			},
			{
				headerName: copy.Tags(),
				field: 'tags',
				type: 'badge',
				flex: 2,
				valueGetter: ({ data }) => data?.tags ?? []
			},
			{
				headerName: copy.UsedBy(),
				colId: 'usages',
				flex: 2,
				sortable: false,
				suppressRowLink: true,
				cellRenderer: ({ data }: ICellRendererParams<Asset>) =>
					data ? (
						<div className='flex h-full w-full items-center overflow-hidden'>
							<AssetUsages usages={data.usages} />
						</div>
					) : null
			},
			{
				headerName: L.Fields.CreatedAt(),
				field: 'createdAt',
				type: 'date',
				flex: 1
			},
			createActionsColumn<Asset>([
				{ icon: <Pencil className='size-4' />, onClick: asset => setEditingAsset(asset) },
				{ icon: <Copy className='size-4' />, onClick: asset => void handleCopyUrl(asset) },
				{ icon: <Trash2 className='size-4' />, onClick: asset => void handleDelete(asset) }
			])
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[L, copy]
	)

	const handleRowClicked = (event: RowClickedEvent<Asset>) => {
		// AG Grid fires this before React click handlers, so ignore clicks that land on the row's own
		// interactive controls (the action buttons) and only open the preview for plain-cell clicks.
		const target = event.event?.target as HTMLElement | null
		if (target?.closest('button, a, input, select, textarea, label')) return
		if (event.data) setPreviewAsset(event.data)
	}

	let mainContent: ReactNode
	if (assetsQuery.isError) {
		mainContent = (
			<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
				<Heading level='h3'>{L.ApiErrorAlert.FailedToLoadData()}</Heading>
				<Text className='max-w-md text-ui-fg-subtle'>{L.ApiErrorAlert.AnErrorOccurredWhileFetchingData()}</Text>
				<Button type='button' variant='secondary' onClick={() => assetsQuery.refetch()}>
					{L.General.TryAgain()}
				</Button>
			</div>
		)
	} else if (showingEmptySuccess) {
		mainContent = (
			<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
				<ImageIcon className='size-10 text-ui-fg-muted' />
				<Heading level='h3'>{copy.NoAssets()}</Heading>
				<Text className='max-w-sm text-ui-fg-subtle'>{copy.NoAssetsDescription()}</Text>
				<Button onClick={() => setUploadOpen(true)}>{copy.Upload()}</Button>
			</div>
		)
	} else {
		mainContent = (
			<div className='flex min-h-0 flex-1 flex-col'>
				{view === 'gallery' ? (
					<div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6'>
						{assets.map(asset => (
							<AssetCard
								key={asset.uuid}
								asset={asset}
								copy={copy}
								onPreview={() => setPreviewAsset(asset)}
								onDelete={() => handleDelete(asset)}
								onCopyUrl={() => handleCopyUrl(asset)}
								onEditTags={() => setEditingAsset(asset)}
							/>
						))}
					</div>
				) : (
					<Table<Asset>
						className='w-full flex-1'
						data={assets}
						columnDefs={columnDefs}
						getRowId={asset => asset.uuid}
						onRowClicked={handleRowClicked}
						rowClass='cursor-pointer'
						suppressCellFocus
					/>
				)}
				<div className='py-4'>
					<Pagination totalCount={assetsQuery.data?.count} />
				</div>
			</div>
		)
	}

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={heading}
					rightSide={
						<div className='flex items-center gap-1'>
							<SearchField accessibleLabel={copy.SearchFilename()} />
							<FilterButton onClick={() => setFiltersOpen(true)} activeCount={activeFilterCount} />
							<div className='mx-1 flex items-center rounded-md border border-ui-border-base p-0.5'>
								<IconButton
									variant={view === 'table' ? 'primary' : 'transparent'}
									size='small'
									tooltipText={copy.TableView()}
									onClick={() => setView('table')}
								>
									<List className='size-5' />
								</IconButton>
								<IconButton
									variant={view === 'gallery' ? 'primary' : 'transparent'}
									size='small'
									tooltipText={copy.GalleryView()}
									onClick={() => setView('gallery')}
								>
									<LayoutGrid className='size-5' />
								</IconButton>
							</div>
							<IconButton tooltipText={copy.ManageTags()} onClick={() => setManageTagsOpen(true)}>
								<Tags className='size-5' />
							</IconButton>
							<IconButton tooltipText={copy.Upload()} onClick={() => setUploadOpen(true)}>
								<Plus />
							</IconButton>
						</div>
					}
				/>
				<div className='flex flex-1 flex-col overflow-y-auto p-4 sm:p-6'>{mainContent}</div>
			</Container>
			<UploadDialog
				open={uploadOpen}
				onOpenChange={setUploadOpen}
				suggestedTags={allTags}
				category={category}
				copy={copy}
			/>
			<ManageTagsDialog
				open={manageTagsOpen}
				onOpenChange={setManageTagsOpen}
				category={category}
				copy={copy}
				onRemovedTagFromFilter={tag => {
					if (selectedTags.includes(tag)) {
						navigate({
							to: '.',
							search: (prev: any) => ({ ...prev, tags: selectedTags.filter(t => t !== tag), offset: 0 })
						})
					}
				}}
				onRenamedTagFromFilter={(from, to) => {
					if (selectedTags.includes(from)) {
						navigate({
							to: '.',
							search: (prev: any) => ({
								...prev,
								tags: selectedTags.map(t => (t === from ? to : t)),
								offset: 0
							})
						})
					}
				}}
			/>
			<EditTagsDialog
				asset={editingAsset}
				suggestedTags={allTags}
				copy={copy}
				onClose={() => setEditingAsset(null)}
			/>
			<AssetAdvancedFilterDrawer
				open={filtersOpen}
				onOpenChange={setFiltersOpen}
				isMarketing={isMarketing}
				tagOptions={tagOptions}
				tagsLabel={copy.Tags()}
				selectedTags={selectedTags}
				usageLabel={copy.UsageFilter()}
				usageOptions={usageOptions}
				selectedUsage={usageFilter ?? ''}
				channelLabel={copy.ChannelFilter()}
				channelOptions={channelOptions}
				selectedChannels={selectedChannels}
				contentLabel={copy.ContentFilter()}
				contentOptions={contentOptions}
				selectedContent={selectedContent}
				contentSearchValue={contentSearch}
				onContentSearchChange={setContentSearch}
				contentLoading={contentQuery.isFetching}
				onApply={applyFilters}
			/>
			<AssetPreviewDialog asset={previewAsset} onClose={() => setPreviewAsset(null)} />
		</SingleColumnPage>
	)
}

function AssetPreviewDialog({ asset, onClose }: { asset: Asset | null; onClose: () => void }) {
	const fullUrl = asset ? pickFullPreviewUrl(asset) : undefined
	return (
		<Dialog open={asset !== null} onOpenChange={open => !open && onClose()}>
			<DialogContent
				className={clx(
					'max-h-[min(90vh,900px)] max-w-[min(calc(100vw-2rem),min(96vw,1200px))] gap-0 overflow-hidden border-ui-border-base bg-ui-bg-base p-3 sm:p-4',
					'sm:max-w-[min(96vw,1200px)]'
				)}
			>
				<DialogTitle className='sr-only'>{asset?.filename}</DialogTitle>
				{fullUrl ? (
					<img src={fullUrl} alt='' className='mx-auto max-h-[min(85vh,860px)] w-full object-contain' />
				) : null}
			</DialogContent>
		</Dialog>
	)
}

type AssetFilterApply = { tags: string[]; usage?: string; channels?: string[]; content?: string[] }

function AssetAdvancedFilterDrawer({
	open,
	onOpenChange,
	isMarketing,
	tagOptions,
	tagsLabel,
	selectedTags,
	usageLabel,
	usageOptions,
	selectedUsage,
	channelLabel,
	channelOptions,
	selectedChannels,
	contentLabel,
	contentOptions,
	selectedContent,
	contentSearchValue,
	onContentSearchChange,
	contentLoading,
	onApply
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	isMarketing: boolean
	tagOptions: { value: string; label: string }[]
	tagsLabel: string
	selectedTags: string[]
	usageLabel: string
	usageOptions: { value: string; label: string }[]
	selectedUsage: string
	channelLabel: string
	channelOptions: { value: string; label: string }[]
	selectedChannels: string[]
	contentLabel: string
	contentOptions: { value: string; label: string }[]
	selectedContent: string[]
	contentSearchValue: string
	onContentSearchChange: (value: string) => void
	contentLoading: boolean
	onApply: (next: AssetFilterApply) => void
}) {
	const form = useAppForm({
		defaultValues: {
			tags: selectedTags,
			usage: selectedUsage,
			channels: selectedChannels,
			content: selectedContent
		},
		onSubmit: async ({ value }) => {
			// Live listeners keep the sentinel exclusive; normalize once more defensively before submit.
			onApply({
				tags: value.tags,
				usage: isMarketing ? undefined : value.usage,
				channels: isMarketing ? normalizeSentinel(value.channels) : undefined,
				content: isMarketing ? normalizeSentinel(value.content) : undefined
			})
			onOpenChange(false)
		}
	})

	// Keep FILTER_NONE mutually exclusive with concrete uuids as the user toggles options. The combobox
	// appends the newest selection, so the last element wins: picking the sentinel clears uuids, and
	// picking a uuid clears the sentinel. Applied via a field listener since the field never holds a
	// mixed array (idempotent, so re-setting the normalized value doesn't loop).
	const enforceSentinelExclusivity = (name: 'channels' | 'content', value: string[]) => {
		const normalized = normalizeSentinel(value)
		const changed = normalized.length !== value.length || normalized.some((v, i) => v !== value[i])
		if (changed) form.setFieldValue(name, normalized)
	}

	// Sync the form back to the URL's filters whenever the drawer is (re)opened, so it reflects
	// filters changed elsewhere (e.g. a tag removed/renamed via Manage tags).
	useEffect(() => {
		if (open) {
			form.reset({
				tags: selectedTags,
				usage: selectedUsage,
				channels: selectedChannels,
				content: selectedContent
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	return (
		<form.AppForm>
			<AdvancedFilterDrawer
				open={open}
				onOpenChange={onOpenChange}
				onClearAll={() => {
					onApply({ tags: [], usage: undefined, channels: [], content: [] })
					onOpenChange(false)
				}}
				onApplyClick={() => form.handleSubmit()}
			>
				<form.AppField name='tags'>
					{field => <field.ComboboxField label={tagsLabel} options={tagOptions} optional />}
				</form.AppField>
				{isMarketing ? (
					<>
						<form.AppField
							name='channels'
							listeners={{ onChange: ({ value }) => enforceSentinelExclusivity('channels', value) }}
						>
							{field => <field.ComboboxField label={channelLabel} options={channelOptions} optional />}
						</form.AppField>
						<form.AppField
							name='content'
							listeners={{ onChange: ({ value }) => enforceSentinelExclusivity('content', value) }}
						>
							{field => (
								<field.ComboboxField
									label={contentLabel}
									options={contentOptions}
									searchValue={contentSearchValue}
									onSearchValueChange={onContentSearchChange}
									optionsLoading={contentLoading}
									optional
								/>
							)}
						</form.AppField>
					</>
				) : (
					<form.AppField name='usage'>
						{field => <field.SingleComboboxField label={usageLabel} options={usageOptions} optional />}
					</form.AppField>
				)}
			</AdvancedFilterDrawer>
		</form.AppForm>
	)
}

function ManageTagsDialog({
	open,
	onOpenChange,
	category,
	copy,
	onRemovedTagFromFilter,
	onRenamedTagFromFilter
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	category: AssetCategoryType
	copy: LibraryCopy
	onRemovedTagFromFilter: (tag: string) => void
	onRenamedTagFromFilter: (from: string, to: string) => void
}) {
	const { L } = useI18n()
	const [draftTag, setDraftTag] = useState('')
	const [editingTag, setEditingTag] = useState<string | null>(null)
	const [renameDraft, setRenameDraft] = useState('')
	const tagsQuery = useListAssetTags({ category }, { enabled: open })
	const createTag = useCreateAssetTag(true)
	const renameTag = useRenameAssetTag(true)
	const removeTag = useRemoveTagFromAssets(true)

	const tagRows = tagsQuery.data?.tags ?? []

	useEffect(() => {
		if (!open) {
			setEditingTag(null)
			setRenameDraft('')
		}
	}, [open])

	const cancelRename = () => {
		setEditingTag(null)
		setRenameDraft('')
	}

	const handleCreate = async () => {
		const name = draftTag.trim()
		if (!name) return
		if (tagRows.some(row => row.name === name)) {
			setDraftTag('')
			return
		}
		try {
			await createTag.mutateAsync({ body: { name, category } })
			toast.success(copy.CreateTagSuccess({ tag: name }))
			setDraftTag('')
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleRename = async () => {
		if (!editingTag) return
		const newName = renameDraft.trim()
		if (!newName || newName === editingTag) return

		const sourceRow = tagRows.find(row => row.name === editingTag)
		const targetRow = tagRows.find(row => row.name === newName)

		if (targetRow && (sourceRow?.assetCount ?? 0) > 0) {
			if (
				!window.confirm(
					copy.RenameTagMergeConfirm({ from: editingTag, to: newName, count: targetRow.assetCount })
				)
			) {
				return
			}
		}

		try {
			const res = await renameTag.mutateAsync({ body: { fromName: editingTag, toName: newName, category } })
			if (!res.isSuccess) {
				const msg = typeof res.response === 'string' ? res.response : L.General.Unknown()
				toast.error(msg)
				return
			}
			toast.success(copy.RenameTagSuccess({ from: editingTag, to: newName }))
			onRenamedTagFromFilter(editingTag, newName)
			cancelRename()
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleRemove = async (tag: string) => {
		if (!window.confirm(copy.RemoveTagConfirm({ tag }))) return
		try {
			const res = await removeTag.mutateAsync({ body: { tag, category } })
			if (!res.isSuccess) {
				const msg = typeof res.response === 'string' ? res.response : L.General.Unknown()
				toast.error(msg)
				return
			}
			toast.success(copy.RemoveTagSuccess({ count: res.response.removedFromAssetCount }))
			onRemovedTagFromFilter(tag)
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='border-ui-border-base bg-ui-bg-base sm:max-w-md'>
				<DialogHeader>
					<DialogTitle>{copy.ManageTags()}</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-2'>
					<Input
						placeholder={copy.CreateTagPlaceholder()}
						value={draftTag}
						onChange={e => setDraftTag(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault()
								void handleCreate()
							}
						}}
					/>
					<Button
						type='button'
						size='small'
						variant='secondary'
						className='self-start'
						isLoading={createTag.isPending}
						disabled={!draftTag.trim() || createTag.isPending}
						onClick={() => void handleCreate()}
					>
						{copy.CreateTag()}
					</Button>
				</div>
				{tagsQuery.isLoading ? (
					<Text className='text-sm text-ui-fg-subtle'>{L.General.Loading()}</Text>
				) : tagsQuery.isError ? (
					<div className='flex flex-col gap-2'>
						<Text className='text-sm text-ui-fg-subtle'>
							{L.ApiErrorAlert.AnErrorOccurredWhileFetchingData()}
						</Text>
						<Button type='button' variant='secondary' size='small' onClick={() => tagsQuery.refetch()}>
							{L.General.TryAgain()}
						</Button>
					</div>
				) : tagRows.length === 0 ? (
					<Text className='text-sm text-ui-fg-subtle'>{copy.ManageTagsEmpty()}</Text>
				) : (
					<div className='max-h-[min(60vh,360px)] space-y-2 overflow-y-auto pr-1'>
						{tagRows.map(({ name: tag, assetCount: count }) => {
							const isEditing = editingTag === tag
							return (
								<div
									key={tag}
									className='flex min-w-0 items-center justify-between gap-2 rounded-md border border-ui-border-base px-2 py-1.5'
								>
									<div className='flex min-w-0 flex-1 items-center gap-2'>
										{isEditing ? (
											<Input
												size='small'
												value={renameDraft}
												onChange={e => setRenameDraft(e.target.value)}
												onKeyDown={e => {
													if (e.key === 'Enter') {
														e.preventDefault()
														void handleRename()
													} else if (e.key === 'Escape') {
														e.preventDefault()
														cancelRename()
													}
												}}
												className='min-w-0 flex-1'
												autoFocus
											/>
										) : (
											<Badge size='2xsmall' color='grey' className='min-w-0 max-w-[55%] shrink'>
												<span className='block truncate' title={tag}>
													{tag}
												</span>
											</Badge>
										)}
										<Text className='shrink-0 text-xs text-ui-fg-subtle'>
											{copy.ManageTagsAssetCount({ count })}
										</Text>
									</div>
									{isEditing ? (
										<div className='flex shrink-0 items-center gap-1'>
											<Button
												type='button'
												size='small'
												variant='secondary'
												isLoading={renameTag.isPending}
												disabled={
													!renameDraft.trim() ||
													renameDraft.trim() === tag ||
													renameTag.isPending
												}
												onClick={() => void handleRename()}
											>
												{L.General.Save()}
											</Button>
											<Button
												type='button'
												size='small'
												variant='transparent'
												disabled={renameTag.isPending}
												onClick={cancelRename}
											>
												{L.General.Cancel()}
											</Button>
										</div>
									) : (
										<div className='flex shrink-0 items-center gap-0.5'>
											<IconButton
												variant='transparent'
												size='small'
												tooltipText={copy.RenameTag()}
												disabled={
													removeTag.isPending || renameTag.isPending || editingTag !== null
												}
												onClick={() => {
													setEditingTag(tag)
													setRenameDraft(tag)
												}}
											>
												<Pencil className='size-3.5' />
											</IconButton>
											<IconButton
												variant='transparent'
												size='small'
												tooltipText={L.General.Delete()}
												disabled={
													removeTag.isPending || renameTag.isPending || editingTag !== null
												}
												onClick={() => handleRemove(tag)}
											>
												<Trash2 className='size-3.5' />
											</IconButton>
										</div>
									)}
								</div>
							)
						})}
					</div>
				)}
				<DialogFooter>
					<Button type='button' variant='secondary' onClick={() => onOpenChange(false)}>
						{L.General.Close()}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function AssetCard({
	asset,
	copy,
	onPreview,
	onDelete,
	onCopyUrl,
	onEditTags
}: {
	asset: Asset
	copy: LibraryCopy
	onPreview: () => void
	onDelete: () => void
	onCopyUrl: () => void
	onEditTags: () => void
}) {
	const thumbUrl = pickThumbUrl(asset)

	return (
		<div className='group relative flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-ui-border-base bg-ui-bg-base'>
			<div className='relative w-full min-w-0'>
				<button
					type='button'
					onClick={onPreview}
					className='block w-full min-w-0 cursor-zoom-in text-left outline-none focus-visible:ring-2 focus-visible:ring-ui-border-interactive'
					aria-label={copy.ViewFullImage()}
				>
					<div className='aspect-square w-full min-w-0 overflow-hidden bg-ui-bg-subtle'>
						{thumbUrl ? (
							<img
								src={thumbUrl}
								alt=''
								className='h-full w-full object-cover'
								loading='lazy'
								decoding='async'
							/>
						) : null}
					</div>
				</button>
				<div
					className={clx(
						'absolute right-1 top-1 z-10 flex gap-0.5 rounded-md border border-ui-border-base bg-white p-0.5 shadow-sm',
						'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100'
					)}
				>
					<IconButton
						variant='transparent'
						size='small'
						tooltipText={copy.EditTags()}
						onClick={e => {
							e.stopPropagation()
							onEditTags()
						}}
					>
						<Pencil className='size-3.5' />
					</IconButton>
					<IconButton
						variant='transparent'
						size='small'
						tooltipText={copy.CopyUrl()}
						onClick={e => {
							e.stopPropagation()
							onCopyUrl()
						}}
					>
						<Copy className='size-3.5' />
					</IconButton>
					<IconButton
						variant='transparent'
						size='small'
						tooltipText={copy.DeleteAsset()}
						onClick={e => {
							e.stopPropagation()
							onDelete()
						}}
					>
						<Trash2 className='size-3.5' />
					</IconButton>
				</div>
			</div>
			<div className='flex min-h-0 min-w-0 flex-1 flex-col gap-1.5 border-t border-ui-border-base p-2 sm:p-2.5'>
				<p
					className='txt-compact-xsmall-plus line-clamp-4 min-w-0 break-words text-ui-fg-base'
					title={asset.filename}
				>
					{asset.filename}
				</p>
				<div className='min-w-0 shrink-0'>
					<StatusCell color={statusColor(asset.status)}>{asset.status}</StatusCell>
				</div>
				{asset.tags.length > 0 && (
					<div className='flex min-w-0 flex-wrap gap-1'>
						{asset.tags.map(tag => (
							<Badge key={tag} size='2xsmall' color='grey' className='max-w-full truncate'>
								<span className='truncate' title={tag}>
									{tag}
								</span>
							</Badge>
						))}
					</div>
				)}
				{asset.usages && asset.usages.length > 0 && (
					<div className='min-w-0'>
						<AssetUsages usages={asset.usages} />
					</div>
				)}
			</div>
		</div>
	)
}

function UploadDialog({
	open,
	onOpenChange,
	suggestedTags,
	category,
	copy
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	suggestedTags: string[]
	category: AssetCategoryType
	copy: LibraryCopy
}) {
	const { L } = useI18n()
	const [tags, setTags] = useState<string[]>([])
	const [files, setFiles] = useState<File[]>([])
	const [isDragging, setIsDragging] = useState(false)
	const fileInputRef = useRef<HTMLInputElement | null>(null)
	const upload = useAssetUpload({ category })

	useEffect(() => {
		if (!open) {
			setTags([])
			setFiles([])
			setIsDragging(false)
			upload.reset()
		}
	}, [open, upload.reset])

	const handleFiles = (incoming: FileList | File[] | null) => {
		if (!incoming) return
		const arr = Array.from(incoming).filter(f => f.type.startsWith('image/'))
		if (arr.length === 0) return
		setFiles(prev => [...prev, ...arr])
	}

	const handleSubmit = async () => {
		if (files.length === 0) return
		try {
			for (const file of files) {
				await upload.upload(file, { tags: tags.length > 0 ? tags : undefined })
			}
			onOpenChange(false)
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='border-ui-border-base bg-ui-bg-base'>
				<DialogHeader>
					<DialogTitle>{copy.UploadAssets()}</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<TagsEditor tags={tags} onChange={setTags} suggested={suggestedTags} copy={copy} />
					<button
						type='button'
						onClick={() => fileInputRef.current?.click()}
						onDragOver={e => {
							e.preventDefault()
							setIsDragging(true)
						}}
						onDragLeave={() => setIsDragging(false)}
						onDrop={e => {
							e.preventDefault()
							setIsDragging(false)
							handleFiles(e.dataTransfer.files)
						}}
						className={clx(
							'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center transition-colors',
							isDragging
								? 'border-ui-border-interactive bg-ui-bg-interactive/10'
								: 'border-ui-border-base hover:bg-ui-bg-base-hover'
						)}
					>
						<UploadCloud className='size-8 text-ui-fg-muted' />
						<Text className='text-sm text-ui-fg-subtle'>{copy.UploadDropzone()}</Text>
						<Button type='button' variant='secondary' size='small'>
							{copy.ChooseFiles()}
						</Button>
						<input
							ref={fileInputRef}
							type='file'
							accept='image/*'
							multiple
							className='hidden'
							onChange={e => handleFiles(e.target.files)}
						/>
					</button>
					{files.length > 0 && (
						<div className='flex flex-col gap-1'>
							{files.map((file, i) => (
								<div
									key={`${file.name}-${i}`}
									className='flex items-center justify-between gap-2 rounded-md border border-ui-border-base px-2 py-1'
								>
									<Text className='truncate text-xs'>{file.name}</Text>
									<IconButton
										type='button'
										variant='transparent'
										size='small'
										tooltipText={L.General.Delete()}
										onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
									>
										<X className='size-3.5' />
									</IconButton>
								</div>
							))}
						</div>
					)}
					{upload.isUploading && (
						<Text className='text-xs text-ui-fg-subtle'>
							{copy.Uploading()}: {upload.progress}%
						</Text>
					)}
				</div>
				<DialogFooter className='gap-2'>
					<Button
						type='button'
						variant='secondary'
						onClick={() => onOpenChange(false)}
						disabled={upload.isUploading}
					>
						{L.General.Cancel()}
					</Button>
					<Button
						type='button'
						onClick={handleSubmit}
						isLoading={upload.isUploading}
						disabled={files.length === 0 || upload.isUploading}
					>
						{copy.Upload()}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function EditTagsDialog({
	asset,
	suggestedTags,
	copy,
	onClose
}: {
	asset: Asset | null
	suggestedTags: string[]
	copy: LibraryCopy
	onClose: () => void
}) {
	const { L } = useI18n()
	const [tags, setTags] = useState<string[]>([])
	const updateAsset = useUpdateAsset(true)

	useEffect(() => {
		if (asset) setTags(asset.tags)
	}, [asset])

	const handleSubmit = async () => {
		if (!asset) return
		try {
			await updateAsset.mutateAsync({ path: { assetUuid: asset.uuid }, body: { tags } })
			onClose()
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	return (
		<Dialog open={asset !== null} onOpenChange={open => !open && onClose()}>
			<DialogContent className='border-ui-border-base bg-ui-bg-base'>
				<DialogHeader>
					<DialogTitle>{copy.EditTags()}</DialogTitle>
				</DialogHeader>
				<TagsEditor tags={tags} onChange={setTags} suggested={suggestedTags} copy={copy} />
				<DialogFooter className='gap-2'>
					<Button type='button' variant='secondary' onClick={onClose} disabled={updateAsset.isPending}>
						{L.General.Cancel()}
					</Button>
					<Button type='button' onClick={handleSubmit} isLoading={updateAsset.isPending}>
						{L.General.Save()}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function TagsEditor({
	tags,
	onChange,
	suggested,
	copy,
	showTextInput = true
}: {
	tags: string[]
	onChange: (next: string[]) => void
	suggested: string[]
	copy: LibraryCopy
	/** When false, only chips and suggested-tag buttons (no freeform field). */
	showTextInput?: boolean
}) {
	const [draft, setDraft] = useState('')

	const addTag = (raw: string) => {
		const next = raw.trim()
		if (!next) return
		if (tags.includes(next)) return
		onChange([...tags, next])
		setDraft('')
	}

	const removeTag = (tag: string) => onChange(tags.filter(t => t !== tag))

	const unusedSuggestions = suggested.filter(s => !tags.includes(s))

	return (
		<div className='flex flex-col gap-2'>
			<div className='flex flex-wrap gap-1'>
				{tags.length === 0 && <Text className='text-xs text-ui-fg-muted'>{copy.NoTags()}</Text>}
				{tags.map(tag => (
					<Badge
						key={tag}
						size='2xsmall'
						color='grey'
						className='inline-flex max-w-full items-center gap-0.5 pl-1.5 pr-0.5'
					>
						<span className='min-w-0 truncate' title={tag}>
							{tag}
						</span>
						<button
							type='button'
							onClick={() => removeTag(tag)}
							className='shrink-0 rounded p-0.5 text-ui-tag-neutral-icon hover:bg-ui-bg-base-hover'
							aria-label={copy.DeleteAsset()}
						>
							<X className='size-3' />
						</button>
					</Badge>
				))}
			</div>
			{showTextInput && (
				<Input
					placeholder={copy.TagsPlaceholder()}
					value={draft}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault()
							addTag(draft)
						} else if (e.key === 'Backspace' && !draft && tags.length > 0) {
							removeTag(tags[tags.length - 1])
						}
					}}
					onBlur={() => draft && addTag(draft)}
				/>
			)}
			{unusedSuggestions.length > 0 && (
				<div className='flex flex-wrap gap-1'>
					{unusedSuggestions.map(tag => (
						<Badge key={tag} asChild size='2xsmall' color='grey'>
							<button type='button' onClick={() => addTag(tag)}>
								+ {tag}
							</button>
						</Badge>
					))}
				</div>
			)}
		</div>
	)
}

// How many linked owners to show inline before collapsing the rest into a "+N" badge.
const MAX_VISIBLE_USAGES = 3

const usageHref = (usage: AssetUsage): string => {
	switch (usage.kind) {
		case 'PRODUCT':
			return `/products/${usage.uuid}`
		case 'PRODUCT_GROUP':
			return `/product-groups/${usage.uuid}`
		case 'PRODUCT_CATEGORY':
			return `/product-categories/${usage.uuid}`
		case 'MARKETING_CONTENT':
			return `/marketing/content/${usage.uuid}`
		case 'FEED_LAYOUT':
			return `/marketing/layout/${usage.uuid}`
		default:
			return '.'
	}
}

// Renders the asset's live consumers as clickable badges. Renders nothing when the asset is unused
// (an empty cell reads as "nothing links here"). `stopPropagation` keeps the link from also opening
// the row's image preview.
function AssetUsages({ usages }: { usages: AssetUsage[] | undefined }) {
	const list = usages ?? []
	if (list.length === 0) return null

	const shown = list.slice(0, MAX_VISIBLE_USAGES)
	const extra = list.length - shown.length

	return (
		<div className='flex min-w-0 flex-wrap items-center gap-1'>
			{shown.map(usage => (
				<Badge
					key={`${usage.kind}:${usage.uuid}`}
					size='2xsmall'
					color='grey'
					asChild
					className='max-w-[10rem] truncate'
				>
					<Link to={usageHref(usage)} title={usage.name} onClick={e => e.stopPropagation()}>
						<span className='truncate'>{usage.name}</span>
					</Link>
				</Badge>
			))}
			{extra > 0 && (
				<Badge size='2xsmall' color='grey'>
					+{extra}
				</Badge>
			)}
		</div>
	)
}

function statusColor(status: AssetStatusType): 'green' | 'orange' | 'grey' | 'red' | 'blue' {
	switch (status) {
		case 'READY':
			return 'green'
		case 'PROCESSING':
		case 'UPLOADED':
			return 'blue'
		case 'PENDING_UPLOAD':
			return 'orange'
		case 'FAILED':
			return 'red'
		default:
			return 'grey'
	}
}

// Thumbnails/table cells want the smallest good-enough derivative first; the preview wants the largest.
const THUMB_PRIORITY = ['THUMBNAIL', 'SMALL', 'MEDIUM', 'LARGE', 'ORIGINAL'] as const
const FULL_PREVIEW_PRIORITY = ['ORIGINAL', 'LARGE', 'MEDIUM', 'SMALL', 'THUMBNAIL'] as const
const COPY_PRIORITY = ['MEDIUM', 'LARGE', 'SMALL', 'THUMBNAIL', 'ORIGINAL'] as const

const pickByPriority = (asset: Asset, priority: readonly string[]): string | undefined => {
	for (const type of priority) {
		const match = asset.variants.find(v => v.type === type)
		if (match) return match.url
	}
	return undefined
}

const pickThumbUrl = (asset: Asset): string | undefined => pickByPriority(asset, THUMB_PRIORITY)
const pickFullPreviewUrl = (asset: Asset): string | undefined => pickByPriority(asset, FULL_PREVIEW_PRIORITY)
const pickCopyUrl = (asset: Asset): string | undefined => pickByPriority(asset, COPY_PRIORITY)
